import type { LoadedModule } from './module-loader';
import type { EkhoNamespace, QueuedCall } from './types';

import { dispatchErrorEvent } from './diagnostics';
import { MODULE_REGISTRY } from './module-registry';

type CleanupFn = () => void;

function teardownAll(loaded: LoadedModule[], cleanups: CleanupFn[]): void {
  // Run extra cleanup functions (page watcher, analytics subscriber) first.
  for (const cleanup of cleanups) {
    try {
      cleanup();
    } catch {
      dispatchErrorEvent('teardown', 'cleanup');
    }
  }
  // Reverse-load order so dependents tear down before their dependencies.
  for (let i = loaded.length - 1; i >= 0; i -= 1) {
    try {
      loaded[i]?.module.teardown();
    } catch {
      dispatchErrorEvent('teardown', loaded[i]?.module.name ?? 'unknown');
    }
  }
  if (typeof window !== 'undefined') {
    delete window.Ekho;
  }
}

/** Bind real API if loaded, fallback (no-op) if not. */
function bindModuleApis(ekho: EkhoNamespace, loaded: LoadedModule[]): void {
  for (const descriptor of MODULE_REGISTRY) {
    const match = loaded.find((m) => m.module.name === descriptor.name);
    const api = match ? descriptor.extractApi(match) : descriptor.fallbackApi;
    (ekho as Record<string, unknown>)[descriptor.name] = api;
  }
}

/** Dispatch a queued call to the live API surface. */
function invoke(ekho: EkhoNamespace, call: QueuedCall): void {
  if (call.module === 'core') {
    const method = (ekho as Record<string, unknown>)[call.method] as
      | ((...args: unknown[]) => void)
      | undefined;
    if (!method) {
      dispatchErrorEvent('invoke', `unknown core method: ${call.method}`);
      return;
    }
    method(...call.args);
    return;
  }
  const surface = (ekho as Record<string, unknown>)[call.module] as
    | Record<string, (...args: unknown[]) => void>
    | undefined;
  if (!surface) {
    dispatchErrorEvent('invoke', `unknown module: ${call.module}`);
    return;
  }
  const method = surface[call.method];
  if (!method) {
    dispatchErrorEvent('invoke', `unknown method: ${call.module}.${call.method}`);
    return;
  }
  method(...call.args);
}

function replayQueue(ekho: EkhoNamespace): void {
  const calls = ekho._queue.splice(0, ekho._queue.length);
  for (const call of calls) invoke(ekho, call);
}

/** Bind real module APIs, wire update/destroy, and replay buffered calls. */
export function activate(
  ekho: EkhoNamespace,
  loaded: LoadedModule[],
  cleanups: CleanupFn[] = [],
): void {
  bindModuleApis(ekho, loaded);
  ekho.update = (context?: unknown): void => ekho._bus.emit('sdk:update', context);
  ekho.destroy = (): void => teardownAll(loaded, cleanups);
  replayQueue(ekho);
}
